import React, { useState, useRef, useEffect, forwardRef } from 'react';
import Information from '../Global/Information';
import currentDomain from '../../Utils/Utils';
import { toast } from 'react-toastify';



const VehicleType = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const types = [
    { label: 'Car', value: 1, info: 'This is a car' },
    { label: 'Truck', value: 2, info: 'This is a truck' },
    { label: 'Motorcycle', value: 3, info: 'This is a motorcycle' },
    { label: 'Special Interest/Other', value: 0, info: 'Special interest or other vehicles' },
  ];

  const selectedType = types.find(type => type.value === value);

  useEffect(() => {
    // If no value is selected yet, set the first option by default.
    // Must be a null/undefined check: "Special Interest/Other" is value 0,
    // and a falsy check would overwrite it with the first option forever.
    if (value == null && types.length > 0) {
      onChange(types[0].value);  // Set the first type as the default
    }
  }, [value, onChange]); // Ensure this runs only when value or onChange changes

  const handleSelect = (selectedValue) => {
    onChange(selectedValue); // Pass the selected value directly
    setIsOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="pt-4 pb-2">
        <div className="flex gap-2">
          <div className="text-lg font-semibold ps-2 pb-1">Vehicle Type</div>
          <Information tooltipText="Select the type of vehicle you are registering." />
        </div>
        <button
          type="button"
          className={`w-full border rounded-md p-2 text-left flex justify-between bg-gray-100 items-center ${error ? 'border-red-500 ' : ' border-gray-300'
            }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{selectedType ? selectedType.label : 'Select a type'}</span>
          <svg
            className={`w-4 h-4 transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-gray-100 rounded-md border border-gray-300 shadow-lg max-h-60">
            {types.map((type) => (
              <div
                key={type.value}
                className="flex gap-3 p-2 cursor-pointer hover:bg-gray-200"
                onClick={() => handleSelect(type.value)}
              >
                <span className="pt-1">{type.label}</span>
                <div>
                  <Information tooltipText={type.info} />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <span className="text-red-500">{error}</span>}
      </div>
    </div>
  );
};

const VehicleClass = React.memo(({ value, onChange, error, index, watchf }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]); // State for available classes
  const dropdownRef = useRef(null);
  const [lastKnownYear, setLastKnownYear] = useState(67);
  const [lastKnownVehicleType, setLastKnownVehicleType] = useState(0);
  const [disableClassSelect, setDisableClassSelect] = useState(true);

  const defaultClasses = [{ label: 'Contact Brady', value: 1, info: '913-704-8131' }];

  const selectedClass = availableClasses.find(classItem => classItem.value === value);

  // Watch the vehicleType value directly
  useEffect(() => {
    const subscription = watchf((data) => {
      const vehicleYear = data.vehicles[index]?.year;
      const vehicleType = data.vehicles[index]?.vehicleType;

      if ((vehicleYear !== lastKnownYear && vehicleYear > 999) || vehicleType !== lastKnownVehicleType) {
        if (vehicleYear > 999) {
          setDisableClassSelect(false)
        }

        setLastKnownVehicleType(vehicleType);
        setLastKnownYear(vehicleYear);
        availVehicleClass(data.vehicles[index])
          .then((availableClasses) => {
            setAvailableClasses(availableClasses); // Update the available classes in state
          })
          .catch((error) => {
            console.error('Error fetching available classes:', error);
            setAvailableClasses(defaultClasses); // Optionally fallback to default classes if there's an error
          });
      }
    });

    return () => subscription.unsubscribe();
  }, [watchf, index, lastKnownYear, lastKnownVehicleType]);

  const handleSelect = (selectedValue) => {
    onChange(selectedValue); // Pass the selected value directly
    setIsOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="pt-4 pb-2">
        <div className="flex gap-2">
          <div className="text-lg font-semibold ps-2 pb-1">Competition Class</div>
          <Information tooltipText="Select the class of vehicle you are registering." />
        </div>
        <button
          type="button"
          className={`w-full border rounded-md p-2 text-left flex justify-between bg-gray-100 items-center ${error ? 'border-red-500' : ' border-gray-300'
            }`}
          disabled={disableClassSelect}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={disableClassSelect ? 'text-gray-400' : ''}>{selectedClass ? selectedClass.label : 'Select a class'}</span>
          <svg
            className={`w-4 h-4 transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-gray-100 rounded-md border border-gray-300 shadow-lg max-h-60 overflow-y-scroll overflow-x-hidden scrollable-dropdown">
            {availableClasses.map((classItem) => (
              <div
                key={classItem.value}
                className="flex gap-3 p-2 cursor-pointer hover:bg-gray-200"
                onClick={() => handleSelect(classItem.value)}
              >
                <span className="pt-1">{classItem.label}</span>
                <div>
                  <Information tooltipText={classItem.info} />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <span className="text-red-500">{error}</span>}
      </div>
    </div>
  );
});

const SingleVehicleClass = ({ value, vehicle, onChange, disableTitle = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]); // State for available classes
  const dropdownRef = useRef(null);

  const defaultClasses = [
    { label: 'Contact Brady', value: 1, info: '913-704-8131' }
  ];

  const [selectedClass, setSelectedClass] = useState(null);

  // Fetch available vehicle classes and set the initial selected class
  useEffect(() => {
    availVehicleClass(vehicle)
      .then((availableClasses) => {
        console.log('Available classes:', availableClasses);
        setAvailableClasses(availableClasses); // Update the available classes in state

        // Set the selected class based on the provided value
        const selected = availableClasses.find((classItem) => classItem.value === value);
        setSelectedClass(selected || null); // Set selectedClass or null if not found
      })
      .catch((error) => {
        console.error('Error fetching available classes:', error);
        setAvailableClasses(defaultClasses); // Fallback to default classes if there's an error
      });
  }, [vehicle, value]); // Add `vehicle` and `value` to dependencies

  const handleSelect = (selectedValue) => {
    const selected = availableClasses.find((classItem) => classItem.value === selectedValue);
    setSelectedClass(selected); // Set the selected class
    onChange(selectedValue, vehicle); // Pass the selected value directly to the parent component
    setIsOpen(false); // Close the dropdown
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const renderDropdown = () => {
    return (
      <div>
        <button
          type="button"
          className={`w-full border rounded-md p-2 text-left flex justify-between bg-gray-100 items-center`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{selectedClass ? selectedClass.label : 'Select a class'}</span>
          <svg
            className={`w-4 h-4 transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div
            className="absolute z-10 mt-1 w-full bg-gray-100 rounded-md border border-gray-300 shadow-lg max-h-60 overflow-y-scroll overflow-x-hidden scrollable-dropdown"
          >
            {availableClasses.map((classItem) => (
              <div
                key={classItem.value}
                className="flex gap-3 p-2 cursor-pointer hover:bg-gray-200"
                onClick={() => handleSelect(classItem.value)}
              >
                <span className="pt-1">{classItem.label}</span>
                <div>
                  <Information tooltipText={classItem.info} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (disableTitle ?
    <>
      <div className="relative w-full" ref={dropdownRef}>
        <div className=" pb-2">
          <div className="flex gap-2">
          </div>
          {renderDropdown()}
        </div>
      </div>
    </> :
    <div className="relative" ref={dropdownRef}>
      <div className="pt-3 pb-2">
        <div className="flex gap-2">
          <div className="text-lg font-semibold ps-2 pb-1">Competition Class</div>
          <Information tooltipText="Select the class of vehicle you are registering." />
        </div>
        {renderDropdown()}
      </div>
    </div>
  );
};


function availParkingZones() {
  // GET to API using promises
  return fetch(`${currentDomain(location)}/api/parking`)
    .then((response) => {
      if (!response.ok)
        throw new Error();
      
      return response.json();
    }).then((data) => {
      const zones = [];
      data.forEach((element) => {
        zones.push({
          name: element.name,
          hexcolor: element.hexcolor,
          id: element.order,
        });
      });
      return zones;
    });
}


const ParkingZones = ({ value, vehicle, onChange, error, displayTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableParkingZones, setAvailableParkingZones] = useState([]); // State for available parking zones
  const dropdownRef = useRef(null);
  const [selectedZone, setSelectedZone] = useState(null);

  // Load parking zones when component mounts
  useEffect(() => {
    availParkingZones().then((zones) => {
      setAvailableParkingZones(zones);
      // Set initial selected zone if value is provided
      if (value) {
        const initialZone = zones.find((zone) => zone.id === value);
        setSelectedZone(initialZone);
      }
    });
  }, [value]);

  const handleSelect = (selectedValue) => {
    const selected = availableParkingZones.find((zone) => zone.id === selectedValue);
    setSelectedZone(selected); // Set the selected zone
    onChange(selectedValue, vehicle); // Pass the selected value directly to the parent component
    setIsOpen(false); // Close the dropdown
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const renderDropdown = () => {
    return (
      <div className='w-full'>
        {displayTitle && (
          <div className="flex gap-2">
            <div className="text-lg font-semibold ps-2 pb-1">Parking Zone</div>
            <Information tooltipText="Select the parking zone for the vehicle." />
          </div>
        )}
        <button
          type="button"
          className={`w-full border rounded-md p-2 text-left flex justify-between bg-gray-100 px-5 items-center ${error ? 'border-red-500' : 'border-gray-300'}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2">
            {selectedZone && (
              <div 
                className="w-4 h-4 border border-gray-400 rounded"
                style={{ backgroundColor: `#${selectedZone.hexcolor}` }}
              ></div>
            )}
            <span>{selectedZone ? selectedZone.name : 'Select parking zone'}</span>
          </div>
          <svg
            className={`w-4 h-4 transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div
            className="absolute z-10 mt-1 w-full bg-gray-100 rounded-md border border-gray-300 shadow-lg max-h-60 overflow-y-scroll overflow-x-hidden scrollable-dropdown"
          >
            {availableParkingZones.map((zone) => (
              <div
                key={zone.id}
                className="flex items-center gap-3 p-2 cursor-pointer hover:bg-gray-200"
                onClick={() => handleSelect(zone.id)}
              >
                <div 
                  className="w-4 h-4 border border-gray-400 rounded"
                  style={{ backgroundColor: `#${zone.hexcolor}` }}
                ></div>
                <span>{zone.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="relative" ref={dropdownRef}>
      <div className="pt-1 pb-2">
        <div className="flex gap-2">
        </div>
        {renderDropdown()}
        {error && <span className="text-red-500">{error}</span>}
      </div>
    </div>
  );
};






function availVehicleClass(vehicle) {
  const { year, vehicleType } = vehicle;

  // GET to API using promises
  return fetch(`${currentDomain(location)}/api/class?exclusive=true`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    }
  })
    .then((response) => {
      if (!response.ok)
        throw new Error();

      return response.json();
    })
    .then((data) => {
      const availableClasses = [];

      // Sort through and ensure year and vehicleType match conditions
      data.forEach((element) => {
        if ((element.syear == null || year >= element.syear) &&
          (element.eyear == null || year <= element.eyear) &&
          (element.vtype == null || element.vtype === vehicleType)) {
          availableClasses.push({
            label: element.name,
            value: element.uuid,
            info: element.desciption,
          });
        }
      });

      return availableClasses;
    })
    .catch((error) => {
      toast.error('Error fetching available classes:');
    });
}

const StateSelect = ({ selectedState, handleStateChange, error, ...rest }) => {
  const states = [
    'Kansas', 'Missouri', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  return (
    <div className="pt-4 pb-2">
      <div className="text-lg font-semibold ps-2 pb-1 w-full">State</div>
      <select
        value={selectedState}
        onChange={(e) => {
          handleStateChange(e);  // Keep your custom handling if needed
          rest.onChange(e); // Call the react-hook-form onChange to trigger validation
        }}
        className={`w-full border border-gray-300 p-2 pb-3 ${error ? 'border-red-500' : ''}`}
        {...rest} // Spread the remaining props which includes react-hook-form methods
      >
        <option disabled value="">Select a state</option>
        {states.map((state) => (
          <option key={state} value={state}>{state}</option>
        ))}
      </select>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// Exporting components under the `Select` object
const Select = {
  VehicleType,
  VehicleClass,
  StateSelect,
  SingleVehicleClass,
  ParkingZones
};

export default Select;
